// src/pages/MemberFiles.js
//
// Track 6 item 6a-06 (2026-09-15, task list section 250; ruling 132). The member's digital file:
// the organising unit is the member, the file holds his matters - every claim, every discipline case,
// each with its documents - and it is how a Local Chairperson holds a caseload (requirements SS6.1).
// The file is DERIVED (ruling 132): this page reads exactly what the officer can already read - his
// claims through claimQueries (the same queries Claims.js and the desks use) and his discipline cases
// through the same query DisciplineCases.js runs for his role - and src\data\memberFile.js groups
// them by member. No new collection, no new rule, no second copy of any matter.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { subscribeVisibleCases } from '../data/disciplineQueries';   // ruling 56 (Record 577)
import { subscribeVisibleClaims } from '../data/claimQueries';
import RulingRef from '../components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { buildMemberFiles, possibleDuplicates, searchFiles } from '../data/memberFile';
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

// caseQuery() removed 2026-10-06 (Record 577): replaced by subscribeVisibleCases (ruling 56).

export default function MemberFiles({ user, onViewClaim, onViewCase, initialOpen }) {
  const [claims, setClaims] = useState(null);
  const [cases, setCases] = useState(null);
  const [err, setErr] = useState('');
  const [text, setText] = useState('');
  const [open, setOpen] = useState(initialOpen || null);   // member key; section 493: a name in the claim tally opens his file
  useEffect(() => {
    if (!user || !user.uid) return undefined;
    const u1 = subscribeVisibleClaims(db, user, rows => setClaims(rows), e => { setErr('Claims could not be read: ' + (e.code || e.message)); setClaims([]); });
    // Ruling 56 (Record 577): one shaped set of discipline queries for every page (src\data\disciplineQueries.js).
    const u2 = subscribeVisibleCases(db, user, onSnapshot, rows => setCases(rows), e => { setErr('Cases could not be read: ' + (e.code || e.message)); setCases([]); });
    return () => { u1 && u1(); u2 && u2(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.uid, user && user.role, user && user.committee, (user && user.scopes || []).join(',')]);

  const files = useMemo(() => buildMemberFiles(claims || [], cases || [], user && user.uid), [claims, cases, user]);
  const dups = useMemo(() => possibleDuplicates(files), [files]);
  const shown = useMemo(() => searchFiles(files, text), [files, text]);
  const file = open ? files.find(f => f.key === open) : null;
  const loading = claims === null || cases === null;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Member files</h1>
        <div className="page-subtitle">One file per member; his claims and discipline cases hang off him, each with its documents.<RulingRef user={user} n={18} why="Every claim and every discipline case sits in its member's own file." /> Derived from the matters you can already read - nothing here is a second copy.<RulingRef user={user} n={132} why="The member's file is derived from his matters, never stored as a second copy." /> A file joined by e-mail or by name says so.</div>
      </div>
      {err ? <div className="alert-banner">&#9888; {err}</div> : null}
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 10, flexWrap: 'wrap' }}>
        <input aria-label="Search by name, e-mail or matter number" className="form-input" style={{ maxWidth: 360 }} placeholder="Search by name, e-mail or matter number" value={text} onChange={e => setText(e.target.value)} />
        <span style={{ fontSize: 12, color: C.dim }}>{loading ? 'Loading…' : files.length + ' file(s) · ' + (claims || []).length + ' claim(s) · ' + (cases || []).length + ' case(s)'}</span>
      </div>
      {dups.length ? (
        <div style={{ fontSize: 12, color: C.accent, border: '1px solid ' + C.accent, padding: '6px 10px', marginBottom: 10 }}>
          {dups.length} pair(s) of files may be one person - not merged, because nothing on the record joins them: {dups.map(d => d.why).join('; ')}. A matter that carries the member's account joins by account; one that carries only a name does not.
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: file ? '1fr 2fr' : '1fr', gap: 14 }}>
        <div className="card">
          {shown.length === 0 && !loading ? <div style={{ fontSize: 12, color: C.dim }}>No member file in your scope{text ? ' matches "' + text + '"' : ''}.</div> : null}
          {shown.map(f => (
            <div key={f.key} {...activate(() => setOpen(f.key))} style={{ cursor: 'pointer', padding: '8px 6px', borderBottom: '1px solid ' + C.line, background: open === f.key ? '#12243A' : 'transparent' }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>{f.name} <span style={{ fontWeight: 400, color: C.dim, fontSize: 11 }}>{f.email || ''}</span></div>
              <div style={{ fontSize: 11, color: C.dim }}>{f.claims} claim(s) · {f.cases} case(s) · {f.open} open · {f.documents} document(s){f.draftsHidden ? ' · ' + f.draftsHidden + ' draft(s) not shown' : ''} · last activity {f.lastActivity || '—'}{f.by !== 'account' ? <span style={{ color: C.accent }}> · joined by {f.by}</span> : null}</div>
            </div>
          ))}
        </div>
        {file ? (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <div className="card-title">{file.name}</div>
                <div className="card-subtitle">{file.email || 'no e-mail on the record'} · {file.railroads.join(', ') || 'railroad not on the matters'} · {file.committees.join(', ') || 'committee not on the matters'} · file joined by {file.joinedBy}</div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setOpen(null)}>Close</button>
            </div>
            {file.draftsHidden ? <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{file.draftsHidden} draft(s) exist and are not shown: a draft is its author's alone.<RulingRef user={user} n={56} why="A draft belongs to its author alone until he submits it for representation." /></div> : null}
            <div className="table-wrapper" style={{ marginTop: 10 }}>
              <table>
                <thead><tr><th>Date</th><th>Matter</th><th>Subject</th><th>Status</th><th>Documents</th><th>Territory</th><th></th></tr></thead>
                <tbody>
                  {file.matters.map(m => (
                    <tr key={m.kind + ':' + m.id}>
                      <td>{m.on || '—'}</td>
                      <td>{m.kind === 'claim' ? 'Claim ' : 'Discipline '}{m.number}</td>
                      <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(m.title || '').slice(0, 120)}</td>
                      <td>{m.status || '—'}{m.outcome ? ' · ' + m.outcome : ''}{m.kind === 'discipline' && m.hearingEvents ? ' · ' + m.hearingEvents + ' hearing act(s)' : ''}</td>
                      <td>{m.documents}</td>
                      <td>{m.scopeKey || '—'}</td>
                      <td>{m.kind === 'claim' && onViewClaim ? <button className="btn btn-secondary btn-sm" onClick={() => onViewClaim(m.id)}>Open</button> : m.kind === 'discipline' && onViewCase ? <button className="btn btn-secondary btn-sm" onClick={() => onViewCase(m.id)}>Open</button> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
